import type { CoreTypes } from '../../../core';
import type { Types as Skola24Types } from '../types';
import type { InternalIdKey } from '../../../core/types/common';
import { getVertexId } from '../../../core/util';
import _locations from './locations';
import _teachers from './teachers';
import _groups from './groups';
import coursesAndEvents from './course-and-events';
import type { BaseOptions } from '../../../common/types';

export namespace OutTypes {
  export type location = Omit<CoreTypes.Serialized.Location, InternalIdKey>;
  export type teacher  = Omit<CoreTypes.Serialized.Teacher,  InternalIdKey>;
  export type group    = Omit<CoreTypes.Serialized.Group,    InternalIdKey>;
  export type course   = Omit<CoreTypes.Serialized.Course,   InternalIdKey>;
  export type event    = Omit<CoreTypes.Serialized.Event,    InternalIdKey>;
}

type Out = {
  locations: OutTypes.location[];
  teachers:  OutTypes.teacher[];
  groups:    OutTypes.group[];
  courses:   OutTypes.course[];
  events:    OutTypes.event[];
  meta?: {
    errors?:   string[];
    warnings?: string[];
  };
};

export default function (
  src:      Skola24Types.schedule,
  _options: Skola24Types.options = { }
): Out {
  const options = Object.assign(_options, { dKey: 'ids' } as BaseOptions);

  // gather all import related warnings/errors
  const warnings: string[] = [];
  const errors:   string[] = [];

  // check which weeks this schedule concern
  if (options?.week == undefined) {
    warnings.push('No events will be imported since no import week was provided');
  }

  ////
  //// construct maps
  ////

  // groups
  const groupMap = new Map(src.school_tblgroup.map(x => [x.PK, x]));

  // courses
  const courseMap = new Map(src.school_tblcourse.map(x => [x.PK, x]));

  // plan
  const planMap = new Map(src.school_tblplan.map(x => [x.PK, x]));

  // subject
  const subjectMap = new Map(src.school_tblsubject.map(x => [x.PK, x.ID]));


  // plan-teacher
  const planTeachersMap = new Map<number, Set<string>>();
  src.school_tblplan_teacher.forEach(x => {
    if (!planTeachersMap.has(x.FKPlan)) planTeachersMap.set(x.FKPlan, new Set());
    planTeachersMap.get(x.FKPlan)!.add(x.FKTeacher.toString());
  });

  // plan-group
  const planGroupsMap = new Map<number, Set<string>>();
  src.school_tblplan_group.forEach(x => {
    if (!planGroupsMap.has(x.FKPlan)) planGroupsMap.set(x.FKPlan, new Set());
    planGroupsMap.get(x.FKPlan)!.add(x.FKGroup.toString());
  });

  // group-plan
  const groupPlansMap = new Map<number, Set<string>>();
  src.school_tblplan_group.forEach(x => {
    if (!groupPlansMap.has(x.FKGroup)) groupPlansMap.set(x.FKGroup, new Set());
    groupPlansMap.get(x.FKGroup)!.add(x.FKPlan.toString());
  });

  // lesson-rooms
  const lessonRoomsMap = new Map<number, Set<string> >();
  src.school_tblschedule_room.forEach(x => {
    if (!lessonRoomsMap.has(x.FKSchedule)) lessonRoomsMap.set(x.FKSchedule, new Set());
    lessonRoomsMap.get(x.FKSchedule)!.add(x.FKRoom.toString());
  });

  // lesson-teacher
  const lessonTeachersMap = new Map<number, Set<string> >();
  src.school_tblschedule_teacher.forEach(x => {
    if (!lessonTeachersMap.has(x.FKSchedule)) lessonTeachersMap.set(x.FKSchedule, new Set());
    lessonTeachersMap.get(x.FKSchedule)!.add(x.FKTeacher.toString());
  });

  // lesson-group
  const lessonGroupsMap = new Map<number, Set<string> >();
  src.school_tblschedule_group.forEach(x => {
    if (!lessonGroupsMap.has(x.FKSchedule)) lessonGroupsMap.set(x.FKSchedule, new Set());
    lessonGroupsMap.get(x.FKSchedule)!.add(x.FKGroup.toString());
  });

  // construct a group (= course) to class map
  const groupClassesMap = new Map<string, Set<string> >();
  src.school_tblgroup_group.forEach(x => {
    if (!groupClassesMap.has(x.FKGroup.toString())) groupClassesMap.set(x.FKGroup.toString(), new Set());
    groupClassesMap.get(x.FKGroup.toString())!.add(x.FKGroupCon.toString());
  });
  // class-class not present in the "school_tblgroup_group" table, so add manually
  src.school_tblgroup.forEach(x => {
    if (x.bClass) {
      if (!groupClassesMap.has(x.PK.toString())) groupClassesMap.set(x.PK.toString(), new Set());
      groupClassesMap.get(x.PK.toString())!.add(x.PK.toString());
    }
  });


  ////
  //// map entities
  ////
  const locations = _locations(src.unit_tblroom,      warnings, errors);
  let   teachers  = _teachers (src.school_tblteacher, warnings, errors);
  let   groups    = _groups   (src.school_tblgroup,   warnings, errors);

  const { courses, events }
    = coursesAndEvents(src.school_tblgroup,
      src.school_tblschedule,
      options,

      groupPlansMap,
      planMap,

      subjectMap,
      planTeachersMap,
      groupClassesMap,
      groups,

      groupMap,
      lessonRoomsMap,
      lessonTeachersMap,
      lessonGroupsMap,
      teachers,

      warnings, errors);


  ////
  //// try filter unreferenced groups and teachers
  ////
  if (options?.removeUnreferencedTeachers) {
    const unreferencedTeacherSet = new Set(teachers.map(x => getVertexId(x, options)));
    events.forEach(e => {
      e.teachers?.forEach(({ to }) => unreferencedTeacherSet.delete(getVertexId(to, options)));
    });
    courses.forEach(c => {
      c.teachers?.forEach(({ to }) => unreferencedTeacherSet.delete(getVertexId(to, options)));
    });
    teachers = teachers.filter(x => !unreferencedTeacherSet.has(getVertexId(x, options)));
  }

  if (options?.removeUnreferencedGroups) {
    const unreferencedGroupSet = new Set(groups.map(x => getVertexId(x, options)));
    events.forEach(e => {
      e.groups?.forEach(({ to }) => unreferencedGroupSet.delete(getVertexId(to, options)));
    });
    courses.forEach(c => {
      c.groups?.forEach(({ to }) => unreferencedGroupSet.delete(getVertexId(to, options)));
    });
    groups = groups.filter(x => !unreferencedGroupSet.has(getVertexId(x, options)));
  }


  return { locations, teachers, groups, courses, events,
    meta: {
      ...( errors.length   ? { errors:   errors   } : { } ),
      ...( warnings.length ? { warnings: warnings } : { } )
    }
  };
}
